/**
 * The task pocket beside its task on a board (spec §6.5). It renders in the
 * board frame's node layer, next to the card rather than inside it, so it
 * can extend past the frame's edge and moves with the board. In a focused
 * board it sits in the lane the board keeps at its right edge. Zooming the
 * old canvas out below 75% moves it to the dock.
 */
import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import { frameFloatsHere, subscribeFrameRects } from "@/lib/frame-screen-rect";
import { isFrameInFocus } from "@/lib/task-focus-tile";
import {
  MIN_ANCHORED_ZOOM,
  measureTaskPocketPosition,
} from "@/lib/task-pocket-placement";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { usePresentationStore } from "@/stores/presentation-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTasksStore } from "@/stores/tasks-store";
import { TaskPocket } from "./task-pocket";
import { useTaskPocketDismiss } from "./use-task-pocket-dismiss";

/**
 * Below 75% zoom the pocket docks, unless the board shows at screen scale in
 * a floating window (or in focus, which the caller checks); the wall is
 * always at 100%.
 */
function boardZoomedOut(boardFrameId: string): boolean {
  if (frameFloatsHere(boardFrameId)) return false;
  return workspaceViewAdapter().transform().zoom < MIN_ANCHORED_ZOOM;
}

export function TaskPocketAnchored({
  boardFrameId,
  cardRef,
}: {
  boardFrameId: string;
  cardRef: RefObject<HTMLElement | null>;
}) {
  const taskId = useTaskPocketStore((s) =>
    s.anchor?.kind === "board" && s.anchor.frameId === boardFrameId
      ? s.taskId
      : null,
  );
  const tasks = useTasksStore((s) => s.tasks);
  const viewMode = useTasksStore((s) => s.boards[boardFrameId]?.view);
  const displayKey = useTasksStore((s) =>
    JSON.stringify([
      s.filterStatus,
      s.filterPriority,
      s.filterAgent,
      s.filterSource,
      s.filterCreatedRange,
      s.showCompleted,
      s.sortBy,
    ]),
  );
  const hasTask = taskId !== null && tasks.some((task) => task.id === taskId);
  const pocketRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  useTaskPocketDismiss(pocketRef, hasTask);

  // Tasks moving between columns or views change where the task sits; only
  // the board holding the open pocket follows pans and frame moves.
  useLayoutEffect(() => {
    const card = cardRef.current;
    const pocket = pocketRef.current;
    if (!hasTask || !taskId || !card || !pocket) {
      setPosition(null);
      return;
    }
    const measure = () => {
      // A focused board is opened: the pocket takes its right-edge lane.
      const opened = isFrameInFocus(
        boardFrameId,
        usePresentationStore.getState().presentation,
      );
      if (!opened && boardZoomedOut(boardFrameId)) {
        useTaskPocketStore.getState().open(taskId, { kind: "docked" });
        return;
      }
      const next = measureTaskPocketPosition(
        card,
        taskId,
        pocket.offsetHeight,
        opened,
      );
      if (!next) {
        useTaskPocketStore.getState().open(taskId, { kind: "docked" });
        return;
      }
      setPosition((current) =>
        current?.left === next.left && current.top === next.top
          ? current
          : next,
      );
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(card);
    observer?.observe(pocket);
    const mutation =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(measure);
    mutation?.observe(card, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    card.addEventListener("scroll", measure, true);
    // Pans, zoom, focus, floating moves and window resizes.
    const unsubscribeRects = subscribeFrameRects(measure);
    return () => {
      observer?.disconnect();
      mutation?.disconnect();
      card.removeEventListener("scroll", measure, true);
      unsubscribeRects();
    };
  }, [boardFrameId, cardRef, hasTask, taskId, tasks, viewMode, displayKey]);

  if (!hasTask || !taskId) return null;
  return (
    <TaskPocket
      key={taskId}
      ref={pocketRef}
      taskId={taskId}
      boardFrameId={boardFrameId}
      className="nodrag nowheel absolute max-h-[calc(100%-24px)]"
      style={
        position
          ? { left: position.left, top: position.top }
          : { left: 0, top: 0, visibility: "hidden" }
      }
    />
  );
}
